import { useSyncExternalStore } from "react";

/** Must match PRIVATE_CACHE in public/sw.js. */
const PRIVATE_CACHE = "websede-private-v1";
const LAST_USER_KEY = "ws_last_user";

/**
 * Registers the service worker in production builds only; in development it would cache Vite's
 * modules and fight hot reloading.
 */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}

/** Deletes the offline copy of the user's mail and other private data. */
export async function clearPrivateCaches(): Promise<void> {
  try {
    if ("caches" in window) await caches.delete(PRIVATE_CACHE);
  } catch {
    // Storage may be unavailable (private windows); there is nothing to clear then.
  }
}

/**
 * Called with the signed-in user's id. A different user on the same browser must never see the
 * previous user's offline mail, so the private cache is cleared when the id changes.
 */
export function rememberUser(userId: string | null): void {
  let previous: string | null = null;
  try {
    previous = localStorage.getItem(LAST_USER_KEY);
    if (userId) localStorage.setItem(LAST_USER_KEY, userId);
    else localStorage.removeItem(LAST_USER_KEY);
  } catch {
    // Without storage we cannot tell users apart; clearing is the safe default.
    void clearPrivateCaches();
    return;
  }
  if (!userId || (previous && previous !== userId)) void clearPrivateCaches();
}

/** Whether a service worker controls this page, i.e. offline copies are being kept. */
export function offlineCacheActive(): boolean {
  return typeof navigator !== "undefined" && Boolean(navigator.serviceWorker?.controller);
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener("online", onChange);
  window.addEventListener("offline", onChange);
  return () => {
    window.removeEventListener("online", onChange);
    window.removeEventListener("offline", onChange);
  };
}

export function useOnline(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => navigator.onLine,
    () => true,
  );
}
